import React, {
  ChangeEvent,
  DragEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import classnames from "classnames";

import AppContainer from "../../container";
import { ApiService, IApiService } from "../../services/api.service";

import AppIcon from "../app-icon";
import Loader from "../loader";

import "./support-modal.scss";

export const MESSAGE_MAX_LENGTH = 150;

/**
 * Keep these aligned with the multer config on `POST /api/support`. They are a
 * courtesy check only — the server is the authority and will reject anything
 * over its own limits.
 */
export const MAX_FILES = 5;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

type SubmitState = "idle" | "loading" | "success" | "error";

export interface Props {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * `SupportModal` is a floating support panel anchored above the help CTA in the
 * bottom-right corner. It deliberately renders no backdrop: the rest of the app
 * stays visible and interactive while it is open, so this is not built on
 * `ModalProvider` / MUI `Dialog`, both of which impose a dimming overlay.
 */
export default function SupportModal(props: Props) {
  const { isOpen, onClose } = props;

  const [message, setMessage] = useState<string>("");
  const [files, setFiles] = useState<File[]>([]);
  const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false);
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const fileInputRef = useRef<HTMLInputElement>(null);

  const isSubmittable = Boolean(message.trim()) || files.length > 0;

  const reset = useCallback(() => {
    setMessage("");
    setFiles([]);
    setIsDraggingOver(false);
    setSubmitState("idle");
    setErrorMessage("");
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  // close on escape, since there is no backdrop to click through
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [handleClose, isOpen]);

  const addFiles = (incoming: FileList | null) => {
    if (!incoming || !incoming.length) {
      return;
    }

    const candidates = Array.from(incoming);
    const accepted = candidates.filter(file => file.size <= MAX_FILE_BYTES);
    const oversized = candidates.length - accepted.length;

    // derive the next list here rather than inside the updater, so the state
    // setter stays free of side effects
    const combined = [...files, ...accepted];
    const overflow = combined.length > MAX_FILES;

    setFiles(overflow ? combined.slice(0, MAX_FILES) : combined);

    if (oversized) {
      setErrorMessage(
        `${oversized} file(s) skipped — each file must be under ${MAX_FILE_BYTES /
          (1024 * 1024)}MB.`
      );
    } else if (overflow) {
      setErrorMessage(`You can attach up to ${MAX_FILES} files.`);
    }
  };

  const removeFile = (index: number) => {
    setFiles(current => current.filter((file, i) => i !== index));
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingOver(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingOver(false);
    addFiles(event.dataTransfer.files);
  };

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    addFiles(event.target.files);
    // allow re-selecting the same file after a removal
    event.target.value = "";
  };

  const handleSubmit = async () => {
    if (!isSubmittable || submitState === "loading") {
      return;
    }

    setSubmitState("loading");
    setErrorMessage("");

    const formData = new FormData();
    formData.append("message", message.trim());
    files.forEach(file => formData.append("attachments", file, file.name));

    const apiService: IApiService = AppContainer.get(ApiService);

    try {
      const response = await apiService.post("/support", formData);

      // @NOTE: ApiService swallows transport errors and resolves `undefined`,
      //        so an absent response is the failure signal we have to read.
      if (!response || response.error) {
        setSubmitState("error");
        setErrorMessage(
          "We couldn't send that. Check your connection and try again."
        );
        return;
      }

      setSubmitState("success");
    } catch (e) {
      setSubmitState("error");
      setErrorMessage(
        "We couldn't send that. Check your connection and try again."
      );
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      aria-labelledby="support-modal-title"
      aria-modal={false}
      className="support-modal"
      role="dialog"
    >
      <div className="support-modal-header">
        <h2 className="support-modal-title" id="support-modal-title">
          Need Help?
        </h2>
        <button
          aria-label="Close"
          className="support-modal-close"
          onClick={handleClose}
          type="button"
        >
          <AppIcon color="dark-gray" size="small" type="close" />
        </button>
      </div>

      {submitState === "success" ? (
        <div className="support-modal-success">
          <AppIcon color="green" size="large" type="checkmark-circle" />
          <p>Thanks! We&rsquo;ll get back to you shortly.</p>
        </div>
      ) : (
        <>
          <p className="support-modal-subtext">
            Provide details and we will respond quickly
          </p>

          <textarea
            className="support-modal-textarea"
            maxLength={MESSAGE_MAX_LENGTH}
            onChange={event =>
              setMessage(event.target.value.slice(0, MESSAGE_MAX_LENGTH))
            }
            placeholder="How can we help?"
            value={message}
          />
          <div className="support-modal-counter">
            {message.length} / {MESSAGE_MAX_LENGTH}
          </div>

          <div className="support-modal-label">Upload images / attachments</div>

          <div
            className={classnames("support-modal-dropzone", {
              "is-dragging": isDraggingOver
            })}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onKeyDown={event => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                if (fileInputRef.current) {
                  fileInputRef.current.click();
                }
              }
            }}
            role="button"
            tabIndex={0}
          >
            <AppIcon color="gray" size="medium" type="add-image" />
            <span>Drag and drop files, or click to browse</span>
          </div>

          <input
            className="support-modal-file-input"
            multiple
            onChange={handleFileInputChange}
            ref={fileInputRef}
            type="file"
          />

          {files.length > 0 && (
            <ul className="support-modal-file-list">
              {files.map((file, index) => (
                <li key={`${file.name}-${file.size}-${index}`}>
                  <span className="support-modal-file-name">{file.name}</span>
                  <button
                    aria-label={`Remove ${file.name}`}
                    className="support-modal-file-remove"
                    onClick={() => removeFile(index)}
                    type="button"
                  >
                    <AppIcon color="dark-gray" size="x-small" type="close" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {submitState === "error" || errorMessage ? (
            <div className="support-modal-error" role="alert">
              {errorMessage}
            </div>
          ) : null}

          <button
            className={classnames("support-modal-submit", {
              "is-disabled": !isSubmittable || submitState === "loading"
            })}
            disabled={!isSubmittable || submitState === "loading"}
            onClick={handleSubmit}
            type="button"
          >
            {submitState === "loading" ? <Loader type="spinner" /> : "Submit"}
          </button>
        </>
      )}
    </div>
  );
}
